// src/components/issues/tableau/FeuilleFiltres.tsx — SCE Issues
// Les filtres au téléphone : une feuille en bas d'écran, des bulles par groupe,
// appliquées au toucher (pas de bouton « Appliquer »). Même modèle de filtres
// que les entonnoirs du bureau : réglages communs, mémorisés de la même façon.

import { Button } from '@/components/ui/Button'
import { Sheet } from '@/components/ui/Sheet'
import type { Issue } from '@/types/issue.types'
import {
  DEFINITIONS_COLONNES,
  basculerValeur,
  compterValeurs,
  filtrer,
  valeursPossibles,
  type ColonneId,
  type ContexteTableau,
  type ReglagesTableau,
} from '@/utils/tableauProblemes'
import { texteValeur } from './valeurs'

const GROUPES: readonly ColonneId[] = ['metier', 'criticite', 'statut', 'phase', 'badge']

export interface FeuilleFiltresProps {
  issues: readonly Issue[]
  reglages: ReglagesTableau
  recherche: string
  ctx: ContexteTableau
  /** Faux pour un fournisseur : ni « Mes déclarations » (il ne voit que les siennes) ni « Suivis ». */
  interne: boolean
  modifier: (changer: (prev: ReglagesTableau) => ReglagesTableau) => void
  onClose: () => void
}

export function FeuilleFiltres({
  issues,
  reglages,
  recherche,
  ctx,
  interne,
  modifier,
  onClose,
}: FeuilleFiltresProps): JSX.Element {
  const n = filtrer(issues, reglages, recherche, ctx).length
  return (
    <Sheet
      titre="Filtres"
      onClose={onClose}
      footer={
        <>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => modifier((r) => ({ ...r, filtres: {}, mesDeclarations: false, suivis: false }))}
          >
            Réinitialiser
          </Button>
          <Button variant="primary" fullWidth onClick={onClose}>
            Voir {n} problème{n > 1 ? 's' : ''}
          </Button>
        </>
      }
    >
      {interne && (
        <div>
          <h4 className="feuille-titre">Mes problèmes</h4>
          <div className="flex flex-wrap gap-2">
            <BulleFiltre
              libelle="Suivis"
              actif={reglages.suivis}
              onClick={() => modifier((r) => ({ ...r, suivis: !r.suivis }))}
            />
            <BulleFiltre
              libelle="Mes déclarations"
              actif={reglages.mesDeclarations}
              onClick={() => modifier((r) => ({ ...r, mesDeclarations: !r.mesDeclarations }))}
            />
          </div>
        </div>
      )}
      {GROUPES.map((col) => {
        const nombres = compterValeurs(issues, reglages, recherche, ctx, col)
        const choisies = reglages.filtres[col] ?? []
        return (
          <div key={col}>
            <h4 className="feuille-titre">{DEFINITIONS_COLONNES[col].libelle}</h4>
            <div className="flex flex-wrap gap-2">
              {valeursPossibles(issues, col, ctx)
                // « À qualifier » est déjà un statut : pas deux fois la même bulle.
                .filter((v) => !(col === 'criticite' && v === 'À qualifier'))
                .map((v) => (
                <BulleFiltre
                  key={v}
                  libelle={`${texteValeur(col, v)} · ${nombres.get(v) ?? 0}`}
                  actif={choisies.includes(v)}
                  onClick={() =>
                    modifier((r) => ({ ...r, filtres: basculerValeur(r.filtres, col, v, !choisies.includes(v)) }))
                  }
                />
              ))}
            </div>
          </div>
        )
      })}
    </Sheet>
  )
}

/** Bulle de filtre (jaune une fois cochée) : reprise par les filtres de la page Équipements. */
export function BulleFiltre({ libelle, actif, onClick }: { libelle: string; actif: boolean; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actif}
      className="px-3 py-2 rounded-full"
      style={{
        fontSize: 'var(--font-size-nav)',
        backgroundColor: actif ? 'var(--color-yellow)' : 'var(--color-gray-5)',
        color: 'var(--color-black)',
        border: actif ? '1px solid var(--color-black)' : '1px solid var(--color-border)',
        fontWeight: actif ? 500 : 400,
      }}
    >
      {libelle}
    </button>
  )
}
